import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getUserRoleLabel, isPlatformAdminRole } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMenu, FiUser, FiLogOut, FiSettings, FiX, FiAlertCircle, FiShield } = FiIcons;

const Header = ({ onToggleSidebar }) => {
  const { 
    currentUser,
    currentUserEmail, 
    currentUserRole, 
    getEffectiveTenantName, 
    currentImpersonatedTenantId, 
    stopImpersonation,
    handleLogout 
  } = useAuth();
  
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleStopImpersonation = () => {
    stopImpersonation();
    setShowUserMenu(false);
  };

  const handleLogoutClick = () => {
    handleLogout();
    setShowUserMenu(false);
  };

  const getRoleLabel = getUserRoleLabel;

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'super_admin': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'bpas_admin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'owner':
      case 'client_admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'staff': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Resolve display name: full_name > name > email
  const displayName = currentUser?.display_name || 
                      currentUser?.name || 
                      (currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}` : null) || 
                      currentUserEmail;

  return (
    <div className="flex flex-col w-full">
      {/* Admin Mode Banner */}
      {(currentImpersonatedTenantId || isPlatformAdminRole(currentUserRole)) && (
        <div className={`w-full px-4 py-1.5 text-xs font-medium flex items-center justify-center space-x-2 ${currentImpersonatedTenantId ? 'bg-orange-50 text-orange-800 border-b border-orange-200' : 'bg-gray-800 text-gray-200 border-b border-gray-700'}`}>
          {currentImpersonatedTenantId ? (
            <>
              <SafeIcon icon={FiAlertCircle} className="w-3.5 h-3.5" />
              <span>Admin Mode — Viewing as Client (Tenant: {getEffectiveTenantName()})</span>
              <button 
                onClick={handleStopImpersonation}
                className="ml-2 bg-white/50 hover:bg-white/80 text-orange-900 rounded px-1.5 py-0.5 text-[10px] transition-colors"
              >
                Exit View
              </button>
            </>
          ) : (
            <span>Admin Mode — {getRoleLabel(currentUserRole)}</span>
          )}
        </div>
      )}

      {/* Main Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onToggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 lg:hidden"
          >
            <SafeIcon icon={FiMenu} className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <h1 className="text-xl font-semibold text-gray-900">BPAS Client Hub</h1>
            {getEffectiveTenantName() && !currentImpersonatedTenantId && (
              <span className="hidden sm:inline-block text-sm text-gray-600 border-l border-gray-300 pl-3">
                {getEffectiveTenantName()}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Role Badge */}
          <div className={`hidden md:flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(currentUserRole)}`}>
            <SafeIcon icon={FiShield} className="w-3 h-3 mr-1.5" />
            {getRoleLabel(currentUserRole)}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 border border-transparent hover:border-gray-200 transition-all"
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                <SafeIcon icon={FiUser} className="w-4 h-4" />
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-700 leading-none">{displayName}</p>
              </div>
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 origin-top-right"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                    <p className="text-xs text-gray-500 mt-1">{currentUserEmail}</p>
                  </div>
                  
                  <div className="py-1">
                    <a 
                      href="#/profile" 
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <SafeIcon icon={FiSettings} className="w-4 h-4 mr-3 text-gray-400" />
                      Profile Settings
                    </a>
                  </div>

                  <div className="border-t border-gray-100 py-1">
                    <button 
                      onClick={handleLogoutClick}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;